import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Bell,
  Bot,
  FolderKanban,
  Kanban,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MailPlus,
  Menu,
  Rocket,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  TextSearch,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { ApiError, api } from '../api/client';
import { meQueryKey, useMe } from '../api/session';
import type { ProjectDetail } from '../api/types';
import { NotificationsDialog } from '../pages/workspace/Notifications';
import { Breadcrumb } from '../ui/Breadcrumb';
import { Avatar } from '../ui/misc';
import { ThemeControl } from '../ui/ThemeControl';
import { useActiveProject } from './activeProject';
import { CrumbProvider, useCurrentCrumbs } from './crumbs';

interface NavEntry {
  label: string;
  to: string;
  icon: LucideIcon;
  end?: boolean;
  /** Shown as a pill when above 0; `countLabel` completes the accessible name, e.g. "3 pending". */
  count?: number;
  countLabel?: string;
}

export function NavGroup({ title, items, action }: { title: string; items: NavEntry[]; action?: ReactNode }) {
  return (
    <div className="nav-group">
      <div className="nav-group-title" title={title}>
        <span className="nav-group-label">{title}</span>
        {action}
      </div>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          className="nav-item"
          aria-label={it.count ? `${it.label}, ${it.count} ${it.countLabel ?? ''}`.trim() : undefined}
        >
          <it.icon size={16} aria-hidden />
          <span>{it.label}</span>
          {!!it.count && (
            <span className="nav-count" aria-hidden>
              {it.count > 99 ? '99+' : it.count}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

export function Wordmark({ size = 26 }: { size?: number }) {
  return (
    <span className="wordmark" style={{ fontSize: size }}>
      Lixionary Jira<span className="dot">.</span>
    </span>
  );
}

/** Below this width the sidebar is a drawer opened from the top bar; keep in step with app.css. */
const drawerQuery = '(max-width: 1024px)';
const sidebarId = 'main-navigation';

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) closeButton.current?.focus();
  }, [open]);
  // The current project, or the last one opened, so its menu stays put on workspace and admin pages.
  const active = useActiveProject(me.user.id);
  const key = active.key;
  const project = useQuery({
    queryKey: ['project', key],
    queryFn: () => api.get<ProjectDetail>(`/projects/${key}`),
    enabled: !!key,
    retry: false,
  });
  const lostAccess =
    project.error instanceof ApiError && (project.error.status === 404 || project.error.status === 403);
  const { remembered, remember, forget } = active;
  const loadedKey = project.data?.key;
  useEffect(() => {
    if (remembered && lostAccess) forget();
    else if (!remembered && loadedKey) remember(loadedKey);
  }, [remembered, lostAccess, loadedKey, remember, forget]);
  const admin = me.user.role === 'admin';
  const base = `/projects/${key}`;
  return (
    <aside id={sidebarId} className={`sidebar${open ? ' open' : ''}`} aria-label="Main navigation">
      <div className="sidebar-head">
        <Wordmark />
        <button
          ref={closeButton}
          type="button"
          className="icon-btn sidebar-close"
          onClick={onClose}
          aria-label="Close navigation"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      <nav className="stack lg" style={{ gap: 24 }}>
        <NavGroup
          title="Workspace"
          items={[
            { label: 'Projects', to: '/projects', icon: FolderKanban, end: true },
            {
              label: 'Approvals',
              to: '/approvals',
              icon: ShieldCheck,
              count: me.pendingApprovals,
              countLabel: 'pending',
            },
            { label: 'My AI connections', to: '/account/ai-connections', icon: Bot },
          ]}
        />
        {key && !lostAccess && (
          <NavGroup
            title={project.data ? `${project.data.name} · ${project.data.key}` : key}
            action={
              remembered ? (
                <button
                  type="button"
                  className="nav-group-close"
                  onClick={forget}
                  aria-label={`Hide ${project.data?.name ?? key} from the sidebar`}
                  title="Hide project menu"
                >
                  <X size={14} aria-hidden />
                </button>
              ) : undefined
            }
            items={[
              { label: 'Dashboard', to: base, icon: LayoutDashboard, end: true },
              { label: 'Board', to: `${base}/board`, icon: Kanban },
              { label: 'Search', to: `${base}/search`, icon: TextSearch },
              { label: 'Releases', to: `${base}/releases`, icon: Rocket },
              { label: 'Activity', to: `${base}/activity`, icon: Activity },
              ...(admin ? [{ label: 'Settings', to: `${base}/settings`, icon: Settings }] : []),
            ]}
          />
        )}
        {admin && (
          <NavGroup
            title="Administration"
            items={[
              { label: 'Users', to: '/admin/users', icon: Users },
              { label: 'Invitations', to: '/admin/invitations', icon: MailPlus },
              { label: 'AI agents', to: '/admin/ai-agents', icon: Bot },
              { label: 'Service accounts', to: '/admin/service-accounts', icon: KeyRound },
              { label: 'Ticket assistant', to: '/admin/assistant', icon: Sparkles },
              { label: 'Audit', to: '/admin/audit', icon: ScrollText },
            ]}
          />
        )}
      </nav>
    </aside>
  );
}

function TopBar({
  menuOpen,
  menuButton,
  onMenu,
}: {
  menuOpen: boolean;
  menuButton: RefObject<HTMLButtonElement | null>;
  onMenu: () => void;
}) {
  const me = useMe();
  const crumbs = useCurrentCrumbs();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = async () => {
    const r = await api
      .post<{ endSessionUrl: string | null }>('/auth/logout')
      .catch(() => ({ endSessionUrl: null }));
    sessionStorage.removeItem('lj_had_session');
    qc.clear();
    if (r.endSessionUrl) window.location.assign(r.endSessionUrl);
    else navigate('/sign-in?signedOut=1');
  };
  const unread = me.unreadNotifications;
  const [inbox, setInbox] = useState(false);
  const closeInbox = useCallback(() => setInbox(false), []);
  return (
    <header className="topbar">
      <button
        ref={menuButton}
        type="button"
        className="icon-btn menu-button"
        onClick={onMenu}
        aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={menuOpen}
        aria-controls={sidebarId}
      >
        <Menu size={18} />
      </button>
      <div className="grow">
        <Breadcrumb items={crumbs} />
      </div>
      <NavLink to="/search" className="search-trigger" aria-label="Search tickets (press /)">
        <Search size={14} aria-hidden />
        <span className="label" style={{ flex: 1 }}>
          Search tickets
        </span>
        <span className="kbd label">/</span>
      </NavLink>
      <button
        type="button"
        className="icon-link"
        onClick={() => setInbox(true)}
        aria-haspopup="dialog"
        aria-label={`Notifications, ${unread} unread`}
      >
        <Bell size={18} aria-hidden />
        {unread > 0 && <span className="count-pill">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {inbox && <NotificationsDialog onClose={closeInbox} />}
      <ThemeControl />
      <div className="row nowrap" style={{ gap: 10 }}>
        <Avatar name={me.user.displayName} />
        <div className="user-name" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span className="small strong">{me.user.displayName}</span>
          <span style={{ fontSize: 12 }} className="muted">
            {me.user.role === 'admin' ? 'Admin' : 'Member'}
          </span>
        </div>
        <button className="icon-btn" onClick={signOut} aria-label="Sign out" title="Sign out">
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}

function useDesktopNotifications(unreadCount: number) {
  const navigate = useNavigate();
  const prevCount = useRef(unreadCount);
  const seenIds = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    if (!initialized.current) {
      initialized.current = true;
      prevCount.current = unreadCount;
      void api
        .get<{ items: { id: string }[] }>('/notifications?unread=true')
        .then((r) => {
          r.items.forEach((it) => seenIds.current.add(it.id));
        })
        .catch(() => undefined);
      return;
    }

    if (unreadCount > prevCount.current) {
      void api
        .get<{ items: { id: string; title: string; body: string; link: string }[] }>(
          '/notifications?unread=true',
        )
        .then((r) => {
          const fresh = r.items.filter((it) => !seenIds.current.has(it.id));
          fresh.forEach((it) => {
            seenIds.current.add(it.id);
            try {
              const note = new Notification(it.title, {
                body: it.body || 'New notification in Lixionary Jira',
              });
              note.onclick = () => {
                window.focus();
                if (it.link) navigate(it.link);
              };
            } catch {
              // Ignore in environments where Notification construction throws
            }
          });
        })
        .catch(() => undefined);
    }
    prevCount.current = unreadCount;
  }, [unreadCount, navigate]);
}

export function AppShell() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => {
    setOpen(false);
    menuButton.current?.focus();
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useDesktopNotifications(me.unreadNotifications);

  useEffect(() => {
    // Poll unread counts and session periodically (every 30s) so notifications trigger in real-time
    const timer = setInterval(() => {
      void qc.invalidateQueries({ queryKey: meQueryKey });
    }, 30_000);
    return () => clearInterval(timer);
  }, [qc]);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    // Widening past the drawer breakpoint shows the sidebar inline, so drop the drawer state with it.
    const wide = window.matchMedia?.(drawerQuery);
    const onResize = () => {
      if (!wide?.matches) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    wide?.addEventListener('change', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      wide?.removeEventListener('change', onResize);
    };
  }, [open, closeMenu]);
  useEffect(() => {
    // Keep unread counts and session state fresh on navigation.
    void qc.invalidateQueries({ queryKey: meQueryKey });
  }, [location.pathname, qc]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) && !t.isContentEditable) {
        e.preventDefault();
        navigate('/search');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
  return (
    <CrumbProvider>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className="shell">
        <Sidebar open={open} onClose={closeMenu} />
        {open && <div className="sidebar-backdrop" onClick={closeMenu} aria-hidden />}
        <div className="main-col">
          <DevAuthBanner />
          <TopBar menuOpen={open} menuButton={menuButton} onMenu={() => setOpen((o) => !o)} />
          <main id="main" tabIndex={-1} style={{ outline: 'none' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </CrumbProvider>
  );
}

function DevAuthBanner() {
  const me = useMe();
  if (!me.devAuth) return null;
  return (
    <div
      role="status"
      style={{
        background: 'rgba(var(--rgb-warning), 0.16)',
        color: 'var(--color-warning-text)',
        fontSize: 12,
        fontWeight: 500,
        padding: '4px 32px',
        borderBottom: '1px solid var(--color-hairline)',
      }}
    >
      Development sign-in is enabled on this server (DEV_AUTH_ENABLED). Never use this configuration in
      production.
    </div>
  );
}
